


import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Typography, Button, IconButton,
  InputAdornment, Dialog, DialogContent, DialogTitle,
  DialogContentText, useMediaQuery, useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectUser } from '../Commonlibs/userSlice';
import Loader from '../Loader/Loader';
// Import assets
import houseLogo from '../assets/lob.png';
import lockIcon from '../assets/lock.png';
import emailIcon from '../assets/email.png';
import leftImage from '../assets/logip.png';

const ChangePassword = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userFromStore = useSelector(selectUser);
  
  // Form state
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [tempToken, setTempToken] = useState(null);
  
  // Dialog state instead of Snackbar
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info'
  });
  
  const handleCloseDialog = () => {
    setDialog({...dialog, open: false});
  };
  
  const showDialog = (title, message, severity = 'info') => {
    setDialog({
      open: true,
      title,
      message,
      severity
    });
    
    // Auto close dialog after 2 seconds
    setTimeout(() => {
      setDialog(prev => ({...prev, open: false}));
    }, 2000);
  };
  
  // Initialize email from store if available
  useEffect(() => {
    if (userFromStore && userFromStore.email) {
      setEmail(userFromStore.email);
    }
    
    if (userFromStore && userFromStore.id) {
      setUserId(userFromStore.id);
    }
  }, [userFromStore]);
  
  // Reset form when dialog is opened
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
    }
  }, [open]);
  
  // Step 1: Request OTP
  const handleRequestOTP = async () => {
    if (!email) {
      showDialog('Error', 'Please enter your email address.', 'error');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showDialog('Error', 'Please enter a valid email address.', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/passwordRequest-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_id: email }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "SUCCESS") {
        setUserId(data.result.user_id);
        setTimeout(() => {
          showDialog('Success', data.message || 'OTP sent to your email!', 'success');
          setCurrentStep(2);
          setLoading(false);
        }, 2000);
      } else {
        setTimeout(() => {
          showDialog('Error', data.message || 'Failed to send OTP. Try again.', 'error');
          setLoading(false);
        }, 2000);
      }
    } catch (error) {
      console.error('OTP request error:', error);
      setTimeout(() => {
        showDialog('Error', 'Something went wrong. Please try again later.', 'error');
        setLoading(false);
      }, 2000);
    }
  };
  
  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp) {
      showDialog('Error', 'Please enter the OTP.', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/passwordVerify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: userId,
          otp: otp 
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "SUCCESS") {
        setTempToken(data.result?.temp_token);
        setTimeout(() => {
          showDialog('Success', data.message || 'OTP verified successfully!', 'success');
          setCurrentStep(3);
          setLoading(false);
        }, 2000);
      } else {
        setTimeout(() => {
          showDialog('Error', data.message || 'Invalid or expired OTP.', 'error');
          setLoading(false);
        }, 2000);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setTimeout(() => {
        showDialog('Error', 'Something went wrong. Please try again later.', 'error');
        setLoading(false);
      }, 2000);
    }
  };
  
  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      showDialog('Error', 'Please fill in all password fields.', 'error');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showDialog('Error', 'Passwords do not match.', 'error');
      return;
    }
    
    if (newPassword.length < 6) {
      showDialog('Error', 'Password must be at least 6 characters.', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/passwordReset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          new_password: newPassword,
          temp_token: tempToken
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "SUCCESS") {
        setTimeout(() => {
          showDialog('Success', data.message || 'Password changed successfully!', 'success');
          setTimeout(() => {
            onClose();
            setCurrentStep(1);
          }, 1200);
          setLoading(false);
        }, 2000);
      } else {
        setTimeout(() => {
          showDialog('Error', data.message || 'Failed to change password. Try again.', 'error');
          setLoading(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setTimeout(() => {
        showDialog('Error', 'Something went wrong. Please try again later.', 'error');
        setLoading(false);
      }, 2000);
    }
  };
  
  // Render form based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <Typography fontSize="16px" mb={1} color="#000000">Enter your email to receive OTP</Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Enter your email address*"
              margin="dense"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="email" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleRequestOTP}
              sx={{
                backgroundColor: '#0052ff',
                color: '#fff',
                py: 1.2,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Get OTP
            </Button>
          </>
        );
      
      case 2:
        return (
          <>
            <Typography fontSize="16px" mb={1} color="#000000">Verify your identity</Typography>
            <TextField
              fullWidth
              disabled
              value={email}
              placeholder="Your email address"
              margin="dense"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="email" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography fontSize="13px" mb={0.5}>Enter OTP</Typography>
            <Typography fontSize="11px" mb={0.5} color="gray">
              (Please check your email for the verification code)
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerifyOTP}
              sx={{
                backgroundColor: '#0052ff',
                color: '#fff',
                py: 1.2,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Verify OTP
            </Button>
          </>
        );
      
      case 3:
        return (
          <>
            <Typography fontSize="16px" mb={1} color="#000000">Create New Password</Typography>
            <TextField
              fullWidth
              disabled
              value={email}
              placeholder="Your email address"
              margin="dense"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="email" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password*"
              margin="dense"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={lockIcon} alt="lock" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password*"
              margin="dense"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={lockIcon} alt="lock" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleResetPassword}
              sx={{
                backgroundColor: '#0052ff',
                color: '#fff',
                py: 1.2,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Change Password
            </Button>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          overflow: 'visible',
          m: 0,
          p: 0,
          height: isMobile ? 'auto' : '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      {loading && <Loader />}
      
      <Dialog
        open={dialog.open}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: '8px',
            width: '400px',
            height: '120px'
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title"
          sx={{ 
            textAlign: 'center',
            color: dialog.severity === 'success' ? '#4caf50' : dialog.severity === 'error' ? '#f44336' : '#0052ff',
            fontWeight: 500,
            pt: 3
          }}
        >
          {dialog.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <DialogContentText 
            id="alert-dialog-description"
            sx={{ textAlign: 'center' }}
          >
            {dialog.message}
          </DialogContentText>
        </DialogContent>
      </Dialog>
      
      <DialogTitle sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#666',
            zIndex: 10
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Left Image Section */}
        {!isMobile && (
          <Box
            width="50%"
            height="100%"
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.3)',
                zIndex: 1
              }}
            />
            <img
              src={leftImage}
              alt="left"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )}
        
        {/* Right Form Section */}
        <Box
          width={isMobile ? '100%' : '50%'}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          px={2}
          py={3}
          sx={{ backgroundColor: '#fff' }}
        >
          <img src={houseLogo} alt="house" style={{ width: 45, height: 45, marginBottom: 10 }} />
          <Typography variant="h6" fontWeight={500} mb={2}>
            {currentStep === 1 ? 'Change Password' : 
             currentStep === 2 ? 'Verify OTP' : 
             'Create New Password'}
          </Typography>
          <Box
            sx={{
              width: '100%',
              maxWidth: 350,
              borderRadius: 3,
              p: 2,
              border: '1px solid #eee',
              boxShadow: '0 0 20px rgba(0,0,0,0.05)'
            }}
          >
            {renderStepContent()}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePassword;